import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

import { setWhatsAppConsent } from "../../whatsappApi";
import { whatsAppFeatureEnabled } from "./access";

/**
 * WhatsAppConsentToggle — per-patient WhatsApp opt-in.
 *
 * POPIA: no outbound WhatsApp message is sent without this, enforced in the
 * send path and the reminder cron. An ABSENT record means not granted, so
 * existing patients default to off until someone captures consent.
 *
 * For a patient who has not been saved yet (no `patientId`) the toggle updates
 * the draft only — there is nothing to PATCH — and the parent persists it with
 * the rest of the form.
 *
 * Renders nothing when the practice is not on a tier that includes WhatsApp:
 * offering to capture consent for a channel the practice cannot use is a switch
 * that does nothing. The server refuses the grant either way.
 */
export default function WhatsAppConsentToggle({
  patientId,
  consent,
  onChange,
  disabled,
}) {
  const [granted, setGranted] = useState(consent?.granted === true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setGranted(consent?.granted === true);
  }, [consent]);

  const toggle = async (next) => {
    setGranted(next);
    onChange?.({ granted: next, source: "practice" });

    if (!patientId) return;

    setSaving(true);
    try {
      await setWhatsAppConsent(patientId, next, "practice");
    } catch (_err) {
      // Put the switch back rather than leaving the UI claiming a consent
      // state the server does not hold.
      setGranted(!next);
      onChange?.({ granted: !next, source: "practice" });
    } finally {
      setSaving(false);
    }
  };

  if (!whatsAppFeatureEnabled()) return null;

  const capturedAt =
    consent?.granted && consent?.grantedAt
      ? new Date(consent.grantedAt).toLocaleDateString("en-GB")
      : null;

  return (
    <div className="d-flex align-items-center gap-2">
      <Form.Check
        type="switch"
        id={`wa-consent-${patientId || "new"}`}
        checked={granted}
        disabled={disabled || saving}
        onChange={(e) => toggle(e.target.checked)}
        label="WhatsApp messages"
      />

      {saving && <Spinner size="sm" animation="border" />}

      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            {capturedAt
              ? `Consent recorded on ${capturedAt}. The patient may opt out at any time by replying STOP.`
              : "The patient must agree before PractoSync can send appointment reminders or messages on WhatsApp."}
          </Tooltip>
        }
      >
        <i className="fa-solid fa-circle-info text-primary" />
      </OverlayTrigger>
    </div>
  );
}
